/**
 * DmlBlastRadiusChip — the click-to-arm row-count display for UPDATE / DELETE
 * (Advisor 2 §A4, finding DML-06). Reads the `DmlEstimateState` from
 * `useDmlRowEstimate` and renders one of:
 *   - `unarmed`  → neutral `Estimate rows` button (arms the probe on click;
 *                  never auto-runs).
 *   - `loading`  → RandomDotmSpinner (per project rule — NOT Loader2).
 *   - `ready`    → exact `≈ 1,204 rows` (locale-grouped, with the "how it was
 *                  counted" tooltip), or the
 *                  join-form `~1,204 matching rows` (labelled approximate with
 *                  a why-tooltip), or the empty-WHERE fusion `all rows (≈ N)`.
 *   - `unavailable` → honest `n/a for this shape` + a teaching tooltip.
 *   - `error`    → `≈ ?` + the timeout tooltip. Execute is never blocked.
 *
 * Intent tone (amber for UPDATE, red for DELETE — matches `DmlTargetCard`) is
 * reserved for the RESULT. The unarmed button, the spinner and the error chip
 * stay neutral, so the colour only appears once there is a number to warn about.
 */

import { RandomDotmSpinner } from '../../../../../components/ui/RandomDotmSpinner';
import type { DmlEstimateState } from '../../execute/useDmlRowEstimate';
import { vqeCopy } from '../../ux/vqeCopy';
import { VqeTooltip } from './VqeTooltip';

export interface DmlBlastRadiusChipProps {
  readonly intent: 'update' | 'delete';
  readonly state: DmlEstimateState;
  /** Join/multi-relation form → the number is a matching-combination count. */
  readonly approximate: boolean;
  /** False when the statement has no WHERE → fuse with the "all rows" warning tone. */
  readonly hasWhere: boolean;
  readonly onArm: () => void;
  readonly testId?: string;
}

interface Tone {
  readonly button: string;
  readonly text: string;
}

const TONE: Record<'update' | 'delete', Tone> = {
  update: {
    button:
      'border-amber-500/60 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10',
    text: 'border-amber-500/50 bg-amber-500/5 text-amber-700 dark:text-amber-400',
  },
  delete: {
    button: 'border-destructive/60 text-destructive hover:bg-destructive/10',
    text: 'border-destructive/50 bg-destructive/5 text-destructive',
  },
};

/** Neutral styling for everything that is not a count yet. */
const NEUTRAL = {
  button: 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
  text: 'border-border/60 bg-muted/40 text-muted-foreground',
};

const CHIP_BASE =
  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium tabular-nums';

export function DmlBlastRadiusChip({
  intent,
  state,
  approximate,
  hasWhere,
  onArm,
  testId = 'vqe-blast-radius-chip',
}: DmlBlastRadiusChipProps) {
  const tone = TONE[intent];

  switch (state.status) {
    case 'unavailable':
      return (
        <VqeTooltip selfProvider content={vqeCopy.dmlEstimate.notApplicable}>
          <span
            data-testid={testId}
            data-state="unavailable"
            className={`${CHIP_BASE} border-border/60 bg-muted/40 text-muted-foreground`}
          >
            {vqeCopy.dmlEstimate.notApplicableLabel}
          </span>
        </VqeTooltip>
      );

    case 'unarmed':
      return (
        <VqeTooltip selfProvider content={vqeCopy.dmlEstimate.armHint}>
          <button
            type="button"
            onClick={onArm}
            data-testid={testId}
            data-state="unarmed"
            className={`${CHIP_BASE} cursor-pointer ${NEUTRAL.button}`}
          >
            {vqeCopy.dmlEstimate.armLabel}
          </button>
        </VqeTooltip>
      );

    case 'loading':
      return (
        <span
          data-testid={testId}
          data-state="loading"
          className={`${CHIP_BASE} ${NEUTRAL.text}`}
        >
          <RandomDotmSpinner size={12} />
          {'≈ …'}
        </span>
      );

    case 'ready': {
      const n = state.count.toLocaleString();
      if (approximate) {
        return (
          <VqeTooltip selfProvider content={vqeCopy.dmlEstimate.approximate}>
            <span
              data-testid={testId}
              data-state="ready"
              data-approximate="true"
              className={`${CHIP_BASE} ${tone.text}`}
            >
              {`~${n} ${vqeCopy.dmlEstimate.matchingRows}`}
            </span>
          </VqeTooltip>
        );
      }
      if (!hasWhere) {
        return (
          <span
            data-testid={testId}
            data-state="ready"
            data-all-rows="true"
            className={`${CHIP_BASE} font-semibold ${tone.text}`}
          >
            {`${vqeCopy.dmlEstimate.allRows} (≈ ${n})`}
          </span>
        );
      }
      return (
        <VqeTooltip selfProvider content={vqeCopy.dmlEstimate.readyHint}>
          <span
            data-testid={testId}
            data-state="ready"
            className={`${CHIP_BASE} ${tone.text}`}
          >
            {`≈ ${n} ${vqeCopy.dmlEstimate.rows}`}
          </span>
        </VqeTooltip>
      );
    }

    case 'error':
      return (
        <VqeTooltip selfProvider content={vqeCopy.dmlEstimate.timeout}>
          <span
            data-testid={testId}
            data-state="error"
            className={`${CHIP_BASE} ${NEUTRAL.text}`}
          >
            {'≈ ?'}
          </span>
        </VqeTooltip>
      );
  }
}
